import omit from 'lodash/fp/omit';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import client from '@/apolloClient';
import type { GroupListItemDataFragment, SubgroupListItemDataFragment } from '@/gql/graphql';
import { success as showSuccessNotification, error as showErrorNotification } from '@/utils/notifications/actions';
import type { SubgroupFormData } from '../shared/SubgroupFormConfig';
import SubgroupFormContainer from '../shared/SubgroupFormContainer';
import '@/utils/notifications';
import { UpdateSubgroupMutation } from './UpdateSubgroup.graphql';
import type { UpdateSubgroupMutationVariables } from './UpdateSubgroup.graphql';

interface UpdateSubgroupProps {
    open: boolean;
    parentGroup: GroupListItemDataFragment | null;
    subgroup: SubgroupListItemDataFragment | null;
    onClose: () => void;
    onCreated: () => void;
}

const EditSubgroup = ({ open, onClose, onCreated, parentGroup, subgroup }: UpdateSubgroupProps) => {
    const { t } = useTranslation('groupDetails');

    const handleSubmit = useCallback(
        async (formData: SubgroupFormData) => {
            try {
                const fullFormData = {
                    ...formData,
                    parentGroupId: parentGroup?.id || '',
                };
                // Omit parentGroupName as it is used only for display and not required by the mutation input
                const variables: UpdateSubgroupMutationVariables = {
                    id: subgroup?.id || '',
                    input: omit(['parentGroupName'], fullFormData),
                };

                const { data } = await client.mutate({
                    mutation: UpdateSubgroupMutation,
                    variables,
                });

                if (data?.updateSubgroup?.__typename === 'UpdateSubgroupSuccessfulResponse') {
                    if (onCreated) {
                        onCreated();
                    } else {
                        onClose();
                    }
                    showSuccessNotification({
                        message: t('notifications.updateSubgroupTitle'),
                        description: t('notifications.updateSubgroupDescription', { name: formData.name }),
                    });
                    return;
                }

                if (data?.updateSubgroup?.__typename === 'ErrorResponse') {
                    const error = {
                        fields: data.updateSubgroup.fields,
                    };
                    showErrorNotification({
                        message: t('notifications.updateErrorTitle'),
                        description: error.fields?.[0]?.message || t('notifications.updateErrorDescription'),
                    });
                    throw error;
                }

                throw new Error('Unknown error creating subgroup');
            } catch (error) {
                console.error('Error creating subgroup:', error);
                // Handle error appropriately
            }
        },
        [t, onCreated, onClose, parentGroup, subgroup]
    );

    return (
        <SubgroupFormContainer
            mode="edit"
            subgroup={subgroup}
            group={parentGroup}
            open={open}
            onClose={onClose}
            onSubmit={handleSubmit}
            onOpenChange={onClose}
            title={t('modalEditSubgroup.title')}
        />
    );
};

export default EditSubgroup;
